import React, {forwardRef, useImperativeHandle, useRef, useState} from 'react';
import {TextInput} from "../../components";
import {Stack, styled} from "@mui/material";
import {useEffectOnce} from "react-use";
import {useLoading} from "../../context";
import {getThemeColor, getTillPaymentJsScript, getTillPublicIntegrationKey} from "../../customHostConfigOnClient";
import {FlashDialog} from "../../components/FlashDialog";
import {CardPaymentResultPopup} from "../../components/CardPaymentResultPopup";

const parseExpirationDate = (expirationYearMonth) => {
  if (!expirationYearMonth) return { month: "", year: "" };

  const [month, year] = expirationYearMonth.split("/").map((val) => val.trim());

  if (!month || !year) return { month: "", year: "" };

  // Normalize year to 4-digit format
  const currentYear = new Date().getFullYear();
  const currentCentury = Math.floor(currentYear / 100) * 100;
  const normalizedYear = year.length === 2 ? currentCentury + Number(year) : Number(year);

  return {
    month: month.padStart(2, "0"), // Ensure 2-digit format
    year: normalizedYear.toString(),
  };
};

const ThreeDSecureIframeWrapper = styled('div')(({ theme }) => ({
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  background: "white",
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[5],
  zIndex: 1300, // Ensure it's above other elements
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}));

const ThreeDSecureIframe = styled("iframe")({
  width: "600px",
  height: "500px",
  border: "none",
});

const TillDivWrapper = styled('div')({
  height: '41px',
  boxSizing: 'border-box',
  paddingRight: '1px',
});

const TillPaymentInlineForm = forwardRef(({ billAmount, billReference, userEmail, initLoadingFinishCallback, creditCardImage }, ref ) => {

  const [firstName, setFirstName] = useState(null);
  const [expirationYearMonth, setExpirationYearMonth] = useState(null);
  const [threeDSecureRedirectUrl, setThreeDSecureRedirectUrl] = useState(null);
  const [flashDialogStatus, setFlashDialogStatus] = useState(false);
  const [flashDialogKey, setFlashDialogKey] = useState(0);
  const paymentRef = useRef(null);
  const {showLoading, hideLoading} = useLoading();

  const refreshFlashDialog = (newStatus) => {
    if (flashDialogStatus === newStatus) setFlashDialogKey(prev => prev + 1);
    else setFlashDialogStatus(newStatus);
  }

  const fetchPaymentStatus = async (paymentId) => {
    const env = new URLSearchParams(window.location.search).get('env');

    while (true) {
      const response = await fetch(`/api/alipay-credit-card/payment/${paymentId}?env=${env}`);
      const data = await response.json();

      if (data.transactionStatus === 'SUCCESS') {
        setThreeDSecureRedirectUrl(prev => (prev !== null ? null : prev));
        return { status: 'PAY_SUCCESS' };
      } else if (data.transactionStatus === 'ERROR') {
        setThreeDSecureRedirectUrl(prev => (prev !== null ? null : prev));
        return { status: 'PAY_ERROR' };
      } else if (data.transactionStatus === 'REDIRECT') {
        const redirectUrl = data.extraData && data.extraData.redirectUrl;
        hideLoading();
        setThreeDSecureRedirectUrl(prev => (prev !== redirectUrl ? redirectUrl : prev));
      }

      // Sleep for 0.5 second before retrying
      await new Promise(resolve => setTimeout(resolve, 500));
    }
  }

  useEffectOnce(() => {
    showLoading();

    // Check if script is already loaded
    if (window.PaymentJs) {
      initTillPayment(); // Call directly if already available
      return;
    }

    // Load script dynamically
    const scriptSrc = getTillPaymentJsScript();

    const existingScript = document.querySelector('script[data-main="payment-js"]');
    if (existingScript) {
      existingScript.remove();
    }

    const script = document.createElement("script");
    script.setAttribute("data-main", "payment-js");
    script.src = scriptSrc;
    script.async = true;

    script.onload = () => {
      initTillPayment();
    };

    document.head.appendChild(script);
  });

  useImperativeHandle(ref, () => ({
    paymentSubmit: (setPaymentStatusCallback, paymentEndpoint, extraPaymentRequestBody) => paymentSubmit(setPaymentStatusCallback, paymentEndpoint, extraPaymentRequestBody),
  }));

  const handleExpirationChange = (value) => {
    // Remove all non-digit and non-slash characters
    let cleaned = value.replace(/[^\d/]/g, '');

    // If user types more than 2 digits and no slash, auto-insert slash
    if (/^\d{3,4}$/.test(cleaned)) {
      cleaned = cleaned.slice(0, 2) + '/' + cleaned.slice(2, 4);
    } else if (/^\d{2}\/?\d{0,2}$/.test(cleaned)) {
      // If user types 2 digits, allow optional slash and up to 2 more digits
      if (cleaned.length === 2) {
        // Do nothing, let user type slash or next digit
      } else if (cleaned.length > 2 && cleaned[2] !== '/') {
        cleaned = cleaned.slice(0, 2) + '/' + cleaned.slice(2, 4);
      }
    }

    // Limit to MM/YY format
    if (cleaned.length > 5) {
      cleaned = cleaned.slice(0, 5);
    }

    setExpirationYearMonth(cleaned);
  };

  const env = new URLSearchParams(window.location.search).get("env");

  const initTillPayment = () => {
    // eslint-disable-next-line no-undef
    let payment = new PaymentJs();

    const publicIntegrationKey = getTillPublicIntegrationKey();

    payment.init(publicIntegrationKey, 'card_number_div_id', 'cvv_div_id', function (paymentt) {
      const inputStyle = {
        'width': '100%',
        'padding': '10px',
        'border-radius': '10px',
        'border': '1px solid #ccc',
        'box-shadow': '0px 1px 4px 0px rgba(0, 0, 0, 0.10)',
        'font-size': '15px',
        'appearance': 'none',
        '-webkit-appearance': 'none',
        '-moz-appearance': 'none',
        'background-clip': 'padding-box'
      }
      const inputFocusStyle = {
        'border-color': getThemeColor(),
        'box-shadow': `0 0 5px ${getThemeColor()}`,
        'outline': 'none'
      }
      const inputBlurStyle = {
        'border-color': '#ccc',
        'box-shadow': `0px 1px 4px 0px rgba(0, 0, 0, 0.10)`,
      }

      // Set the initial style
      paymentt.setNumberStyle(inputStyle);
      paymentt.setNumberPlaceholder('1234 1234 1234 1234');
      paymentt.setCvvStyle(inputStyle);
      paymentt.setCvvPlaceholder('CVV');

      // Focus events
      paymentt.numberOn('focus', function () {
        paymentt.setNumberStyle(inputFocusStyle);
      });
      paymentt.cvvOn('focus', function () {
        paymentt.setCvvStyle(inputFocusStyle);
      });

      // Blur events
      paymentt.numberOn('blur', function () {
        paymentt.setNumberStyle(inputBlurStyle);
      });
      paymentt.cvvOn('blur', function () {
        paymentt.setCvvStyle(inputBlurStyle);
      });

      // payment.numberOn('input', function (data) {
      //   // Handle input event
      //   console.log("Handle input event:", data);
      // });

      hideLoading();
      paymentRef.current = paymentt;
      initLoadingFinishCallback();
    });
  }

  const paymentSubmit = (setPaymentStatusCallback, paymentEndpoint = '/api/alipay-credit-card/create-payment-with-token', extraPaymentRequestBody = {}) => {
    showLoading();

    // https://gateway.tillpayments.com/documentation/connectors for testing data
    const { year, month } = parseExpirationDate(expirationYearMonth);

    paymentRef.current.tokenize(
      {
        card_holder: firstName,
        // first_six_digits: '411111',
        // last_four_digits: '1111',
        month,
        year,
      },
      async function (token, cardData) { //success callback function
        console.log('paymentjs tokenize success', {token, cardData});

        const response = await fetch(`${paymentEndpoint}?env=${env}`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify({
            billAmount,
            billReference,
            userEmail,
            transactionToken: token,
            cardData,
            ...extraPaymentRequestBody,
          }),
        });
        const data = await response.json();
        if (data.return_code === 'SUCCESS') {
          console.log('Payment submit successful!', {data});
          // window.location.href = data.redirect_url;
          // Instead of redirecting, we want to start checking transaction status and render result component in the same page.
          const { status } = await fetchPaymentStatus(data.payment_id);
          setPaymentStatusCallback(status === 'PAY_SUCCESS' ? 'Payment Successful.' : 'Payment Failed.');
          if (status === 'PAY_SUCCESS') {
            // hideLoading('Your payment is successful', 'success');
            hideLoading();
            refreshFlashDialog('PAY_SUCCESS');
          }
          else if (status === 'PAY_ERROR') {
            // hideLoading('Your payment is failed', 'error');
            hideLoading();
            refreshFlashDialog('PAY_ERROR');
          }

        } else {
          // const errorMessage = data.errorMessage || (data.data && data.data.errors && data.data.errors[0].errorMessage);
          // hideLoading('Payment creation is failed. ' + errorMessage, 'error');
          hideLoading();
          refreshFlashDialog('PAY_ERROR');
        }
      },
      function (errors) { //error callback function
        console.error('paymentjs tokenize error occurred', {errors});
        const errorMessage = errors.reduce ? errors.reduce((acc, e) => acc + e.message + '. ', 'Error: Please check your inputs: ') : 'Error: Please check your inputs.';
        hideLoading(errorMessage, 'error');
      }
    );
  }

  return (
    <Stack gap="10px" width="100%">

      <div style={{position: 'relative', marginTop: '30px'}}>
        <label style={{position: 'absolute', left: 5, top: '-28px', fontSize: '13px', fontWeight: '600'}}>Cardholder name</label>
        <TextInput
          placeholder="John Smith"
          value={firstName || ''}
          onChange={setFirstName}
        />
      </div>

      <div style={{width: '100%', position: 'relative', marginTop: '30px'}}>
        <label style={{position: 'absolute', left: 5, top: '-28px', fontSize: '13px', fontWeight: '600'}}>Card number</label>
        <TillDivWrapper id='card_number_div_id'></TillDivWrapper>
        <img style={{position: 'absolute', right: '10px', bottom: '8px', height: '29px'}} src={creditCardImage}/>
      </div>

      <div style={{display: 'flex', alignItems: 'center', marginTop: '30px'}}>
        <div style={{display: 'inline-block', width: '49%', position: 'relative'}}>
          <label style={{position: 'absolute', left: 5, top: '-28px', fontSize: '13px', fontWeight: '600'}}>Expiration date</label>
          <TextInput
            placeholder="MM/YY"
            value={expirationYearMonth || ''}
            onChange={handleExpirationChange}
          />
        </div>
        <div style={{display: 'inline-block', width: '49%', marginLeft: 'auto', position: 'relative'}}>
          <label style={{position: 'absolute', left: 5, top: '-28px', fontSize: '13px', fontWeight: '600'}}>Security code</label>
          <TillDivWrapper id='cvv_div_id'></TillDivWrapper>
        </div>
      </div>

      {/*<FlashDialog key={flashDialogKey} paymentStatus={flashDialogStatus}/>*/}
      <CardPaymentResultPopup key={flashDialogKey} paymentStatus={flashDialogStatus} />

      {threeDSecureRedirectUrl && (
        <ThreeDSecureIframeWrapper>
          <ThreeDSecureIframe src={threeDSecureRedirectUrl} className="secure-frame"/>
        </ThreeDSecureIframeWrapper>
      )}
    </Stack>
  );
});

export {TillPaymentInlineForm};
